import { useState, useEffect, useRef } from 'react';

import { motion } from 'framer-motion';
import {
    Plus, PlusCircle, Database, Edit, Trash2, RefreshCw,
    MoreVertical, Search, Filter, Clock, LayoutGrid, List,
    ArrowRight, Copy, Upload, Download, Brain, ShieldCheck,
    MoreHorizontal, CheckCircle2, AlertCircle, Loader2, Timer, Server
} from 'lucide-react';
import {
    Dialog as MuiDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Typography,
    Box
} from '@mui/material';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from '@/context/useAuth';
import { apiGet, apiDelete, apiPost } from '@/api';
// @ts-ignore
import { StandardPageLayout, Button as CommonButton, ConfirmModal, SortDropdown } from '../Common';
import { Button } from '@/components/ui/button'; // shadcn button
import { useNotification } from '../../context/NotificationContext';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';
import { hasPermission } from '@/utils/permissions';

interface DatabaseItem {
    id: string;
    name: string;
    description?: string;
    datasource_id: string;
    storage_mode: string;
    data_layer: string;
    refresh_schedule?: string;
    status: string;
    created_at: string;
    updated_at: string;
    last_refreshed_at?: string | null;
    current_refresh_status?: string | null;
    tables: any[];
    relationships: any[];
    measures: any[];
    last_refresh_rows?: number | null;
    last_refresh_duration?: number | null;
    domain_id?: string | null;
    domain_name?: string | null;
    pd_applier_config?: any;
    datasource_type?: string | null;
    datasource_name?: string | null;
    source_type?: string | null;
}

interface DataModelsListProps {
    onCreate?: () => void;
    onEdit?: (id: string) => void;
}

export const DataModelsList = ({ onCreate, onEdit }: DataModelsListProps = {}) => {
    const [databases, setDatabases] = useState<DatabaseItem[]>([]);
    const [filteredDatabases, setFilteredDatabases] = useState<DatabaseItem[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const [sortOption, setSortOption] = useState<'updated_desc' | 'updated_asc' | 'name_asc' | 'name_desc'>('updated_desc');

    // Schedule Dialog State
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [scheduleDbId, setScheduleDbId] = useState<string | null>(null);
    const [cronExpression, setCronExpression] = useState('');
    const [scheduleTab, setScheduleTab] = useState<'recurring' | 'interval' | 'custom'>('recurring');
    const [scheduleFrequency, setScheduleFrequency] = useState('daily');
    const [scheduleTime, setScheduleTime] = useState('09:00');
    const [scheduleIntervalValue, setScheduleIntervalValue] = useState(6);
    const [scheduleIntervalUnit, setScheduleIntervalUnit] = useState<'hours' | 'minutes'>('hours');
    const [scheduleWeekDay, setScheduleWeekDay] = useState('1'); // 0=Sun

    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [dbToDelete, setDbToDelete] = useState<string | null>(null);

    // Hard Delete Modal State
    const [isHardDeleteModalOpen, setIsHardDeleteModalOpen] = useState(false);
    const [dbToHardDelete, setDbToHardDelete] = useState<string | null>(null);

    // Blocking dashboards dialog (when deletion is blocked by published dashboards)
    interface BlockingDashboard { id: string; name: string; }
    const [blockingDashboards, setBlockingDashboards] = useState<BlockingDashboard[]>([]);
    const [isBlockingDialogOpen, setIsBlockingDialogOpen] = useState(false);
    const [blockingMessage, setBlockingMessage] = useState('');

    const { token, user } = useAuth();
    const { formatDate } = useTimezoneFormatter();
    const { addNotification } = useNotification() as any;
    const importInputRef = useRef<HTMLInputElement>(null);

    // Connection picker state (shown when import finds the source connection missing)
    const [isConnectionPickerOpen, setIsConnectionPickerOpen] = useState(false);
    const [pendingImportPayload, setPendingImportPayload] = useState<any>(null);
    const [missingConnection, setMissingConnection] = useState<string>('');
    const [availableConnections, setAvailableConnections] = useState<{ id: string; name: string; source?: string }[]>([]);
    const [selectedConnection, setSelectedConnection] = useState<string>('');

    const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const jsonObj = JSON.parse(event.target?.result as string);

                const response = await apiPost('/datamodels/import-json', token, {
                    payload: jsonObj,
                    config: { connection_mappings: {} }
                });

                if (response?.status === 'connection_required') {
                    // Store payload and show connection picker dialog
                    setPendingImportPayload(jsonObj);
                    setMissingConnection(response.missing_connection || '');
                    setAvailableConnections(response.available_connections || []);
                    setSelectedConnection('');
                    setIsConnectionPickerOpen(true);
                } else {
                    addNotification('Data Model imported successfully', 'success');
                    fetchDatabases();
                }
            } catch (error: any) {
                console.error("Import error:", error);
                addNotification(error.message || 'Failed to import JSON', 'error');
            }
        };
        reader.readAsText(file);
        if (importInputRef.current) {
            importInputRef.current.value = '';
        }
    };

    const handleConnectionPickerConfirm = async () => {
        if (!selectedConnection || !pendingImportPayload || !missingConnection) return;
        try {
            await apiPost('/datamodels/import-json', token, {
                payload: pendingImportPayload,
                config: { connection_mappings: { [missingConnection]: selectedConnection } }
            });
            addNotification('Data Model imported successfully', 'success');
            fetchDatabases();
            setIsConnectionPickerOpen(false);
            setPendingImportPayload(null);
        } catch (error: any) {
            console.error('Import with mapping error:', error);
            addNotification(error.message || 'Failed to import data model', 'error');
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        addNotification("Copied to clipboard", "success");
    };

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (refreshingIds.size > 0 && token) {
            interval = setInterval(async () => {
                const nextSet = new Set(refreshingIds);
                const updates: Record<string, any> = {};

                for (const id of Array.from(refreshingIds)) {
                    try {
                        const statusResp = await apiGet(`/datamodels/${id}/refresh-status`, token);
                        if (statusResp && statusResp.refresh_status !== 'RUNNING') {
                            nextSet.delete(id);
                            updates[id] = statusResp;

                            if (statusResp.refresh_status === 'SUCCESS') {
                                addNotification('Data model refreshed successfully', 'success');
                            } else if (statusResp.refresh_status === 'FAILED') {
                                addNotification('Data model refresh failed', 'error');
                            }
                        }
                    } catch (e) {
                        console.error("Failed to poll status for", id);
                    }
                }

                if (Object.keys(updates).length > 0) {
                    setDatabases(prevDbs => prevDbs.map(db => {
                        if (updates[db.id]) {
                            const s = updates[db.id];
                            return {
                                ...db,
                                current_refresh_status: s.refresh_status,
                                last_refreshed_at: s.last_refreshed_at,
                                ...(s.total_rows !== undefined && { last_refresh_rows: s.total_rows }),
                            };
                        }
                        return db;
                    }));
                }
                setRefreshingIds(nextSet);
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [refreshingIds, token, addNotification]);

    useEffect(() => {
        fetchDatabases();
    }, []);

    // Listen for refresh-started events fired by the Dashboard "Refresh with Data Pull" action.
    // This allows the Data Models page to show the "Refreshing…" badge immediately,
    // even when it's already open and hasn't re-fetched the list.
    useEffect(() => {
        const handleExternalRefreshStarted = (e: Event) => {
            const modelId = (e as CustomEvent).detail?.modelId as string | undefined;
            if (!modelId) return;
            setDatabases(prev => prev.map(db =>
                db.id === modelId ? { ...db, current_refresh_status: 'RUNNING' } : db
            ));
            setRefreshingIds(prev => new Set(prev).add(modelId));
        };
        window.addEventListener('datamodel-refresh-started', handleExternalRefreshStarted);
        return () => window.removeEventListener('datamodel-refresh-started', handleExternalRefreshStarted);
    }, []);

    useEffect(() => {
        if (searchQuery) {
            setFilteredDatabases(
                databases.filter(db =>
                    db.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    db.description?.toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        } else {
            setFilteredDatabases(databases);
        }
    }, [searchQuery, databases]);
    const sortedDatabases = [...filteredDatabases].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
        if (sortOption === 'updated_desc') return dateB - dateA;
        if (sortOption === 'updated_asc') return dateA - dateB;
        if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
        if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
        return 0;
    });

    const fetchDatabases = async () => {
        setIsLoading(true);
        try {
            const data = await apiGet('/datamodels', token);

            // Re-apply RUNNING status for any model still tracked in refreshingIds.
            // This prevents a stale server response (e.g. PENDING or null before the
            // worker picks up the job) from clearing a badge we optimistically set.
            setRefreshingIds(prev => {
                const merged = data?.map((db: any) => {
                    if (prev.has(db.id) && db.current_refresh_status !== 'SUCCESS' && db.current_refresh_status !== 'FAILED') {
                        return { ...db, current_refresh_status: 'RUNNING' };
                    }
                    return db;
                }) ?? data ?? [];
                setDatabases(merged);

                // Also add any server-reported RUNNING models that weren't already tracked
                const serverRunning = (data || [])
                    .filter((db: any) => db.current_refresh_status === 'RUNNING')
                    .map((db: any) => db.id);
                const next = new Set(prev);
                serverRunning.forEach((id: string) => next.add(id));
                return next;
            });
        } catch (e) {
            console.error('Failed to fetch databases', e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = () => {
        if (onCreate) onCreate();
    };

    const handleEdit = (id: string) => {
        if (onEdit) onEdit(id);
    };

    const handleOpenSchedule = (db: DatabaseItem) => {
        setScheduleDbId(db.id);
        const cs = db.refresh_schedule;
        if (cs && cs !== 'MANUAL') {
            setCronExpression(cs);
            // Try to detect type from cron pattern
            const parts = cs.split(' ');
            if (parts.length === 5) {
                const [min, hr, , , dow] = parts;
                if (dow === '*' && hr !== '*') {
                    // Daily at specific time
                    setScheduleTab('recurring');
                    setScheduleFrequency('daily');
                    const hh = hr.padStart(2, '0');
                    const mm = min.padStart(2, '0');
                    setScheduleTime(`${hh}:${mm}`);
                } else if (dow !== '*') {
                    setScheduleTab('recurring');
                    setScheduleFrequency('weekly');
                    setScheduleWeekDay(dow);
                    const hh = hr.padStart(2, '0');
                    const mm = min.padStart(2, '0');
                    setScheduleTime(`${hh}:${mm}`);
                } else if (min.startsWith('*/') || hr.startsWith('*/')) {
                    setScheduleTab('interval');
                    if (hr.startsWith('*/')) {
                        setScheduleIntervalValue(parseInt(hr.replace('*/', '')));
                        setScheduleIntervalUnit('hours');
                    } else {
                        setScheduleIntervalValue(parseInt(min.replace('*/', '')));
                        setScheduleIntervalUnit('minutes');
                    }
                } else {
                    setScheduleTab('custom');
                }
            }
        } else {
            // No schedule — reset to defaults
            setScheduleTab('recurring');
            setScheduleFrequency('daily');
            setScheduleTime('09:00');
            setCronExpression('');
        }
        setIsScheduleOpen(true);
    };

    const buildCronFromUI = (): string => {
        if (scheduleTab === 'recurring') {
            const [hh, mm] = scheduleTime.split(':');
            const h = parseInt(hh, 10);
            const m = parseInt(mm, 10);
            if (scheduleFrequency === 'hourly') return `0 * * * *`;
            if (scheduleFrequency === 'daily') return `${m} ${h} * * *`;
            if (scheduleFrequency === 'weekly') return `${m} ${h} * * ${scheduleWeekDay}`;
            if (scheduleFrequency === 'monthly') return `${m} ${h} 1 * *`;
        }
        if (scheduleTab === 'interval') {
            return scheduleIntervalUnit === 'hours'
                ? `0 */${scheduleIntervalValue} * * *`
                : `*/${scheduleIntervalValue} * * * *`;
        }
        // custom tab
        return cronExpression;
    };

    const handleSaveSchedule = async () => {
        if (!scheduleDbId) return;
        try {
            const cron = buildCronFromUI();
            await apiPost(`/datamodels/${scheduleDbId}/schedule`, token, {
                cron_expression: cron,
                timezone: "UTC"
            });
            setIsScheduleOpen(false);
            addNotification('Schedule updated successfully', 'success');
            fetchDatabases();
        } catch (e) {
            console.error(e);
            addNotification('Failed to update schedule', 'error');
        }
    };

    const handleArchive = (id: string) => {
        setDbToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const confirmArchive = async () => {
        if (!dbToDelete) return;

        try {
            await apiDelete(`/datamodels/${dbToDelete}`, token);
            addNotification('Data model archived successfully', 'success');
            fetchDatabases();
            setIsDeleteModalOpen(false);
            setDbToDelete(null);
        } catch (e: any) {
            console.error('Failed to archive database', e);

            let parsedDetail: any = null;
            try {
                const raw = e?.message || '';
                parsedDetail = JSON.parse(raw);
            } catch {
                // Not JSON
            }

            if (parsedDetail?.error === 'data_model_in_use') {
                setIsDeleteModalOpen(false);
                setBlockingDashboards(parsedDetail.blocking_dashboards || []);
                setBlockingMessage(parsedDetail.message || 'This data model is linked to one or more published dashboards.');
                setIsBlockingDialogOpen(true);
            } else {
                addNotification(e.message || 'Failed to archive data model', 'error');
                setIsDeleteModalOpen(false);
                setDbToDelete(null);
            }
        }
    };

    const handleHardDelete = (id: string) => {
        setDbToHardDelete(id);
        setIsHardDeleteModalOpen(true);
    };

    const confirmHardDelete = async () => {
        if (!dbToHardDelete) return;

        try {
            await apiDelete(`/datamodels/${dbToHardDelete}?force=true`, token);
            addNotification('Data model permanently deleted', 'success');
            fetchDatabases();
            setIsHardDeleteModalOpen(false);
            setDbToHardDelete(null);
        } catch (e: any) {
            console.error('Failed to permanently delete database', e);

            let parsedDetail: any = null;
            try {
                const raw = e?.message || '';
                parsedDetail = JSON.parse(raw);
            } catch {
                // Not JSON
            }

            if (parsedDetail?.error === 'data_model_in_use') {
                setIsHardDeleteModalOpen(false);
                setBlockingDashboards(parsedDetail.blocking_dashboards || []);
                setBlockingMessage(parsedDetail.message || 'This data model is linked to one or more published dashboards.');
                setIsBlockingDialogOpen(true);
            } else {
                addNotification(e.message || 'Failed to permanently delete data model', 'error');
                setIsHardDeleteModalOpen(false);
                setDbToHardDelete(null);
            }
        }
    };

    const handleRefresh = async (id: string) => {
        setDatabases(prev => prev.map(db => db.id === id ? { ...db, current_refresh_status: 'RUNNING' } : db));
        setRefreshingIds(prev => new Set(prev).add(id));
        try {
            await apiPost(`/datamodels/${id}/refresh`, token, {});
            addNotification('Data model refresh queued!', 'success');
        } catch (e: any) {
            console.error('Failed to refresh database', e);
            addNotification(e.message || 'Failed to queue refresh', 'error');
            setRefreshingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setDatabases(prev => prev.map(db => db.id === id ? { ...db, current_refresh_status: 'FAILED' } : db));
        }
    };

    const getStatusBadge = (status: string) => {
        const styles = {
            draft: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
            active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
            archived: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
        };
        return styles[status as keyof typeof styles] || styles.draft;
    };

    const getStorageModeBadge = (mode: string) => {
        return mode === 'import'
            ? 'bg-purple-500/10 text-purple-500 border-purple-500/20'
            : 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    };

    const formatDuration = (seconds?: number | null) => {
        if (seconds === null || seconds === undefined) return '';
        if (seconds < 60) return `${Math.round(seconds)}s`;

        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.round(seconds % 60);

        const parts = [];
        if (h > 0) parts.push(`${h}h`);
        if (m > 0) parts.push(`${m}m`);
        if (s > 0 || (h === 0 && m === 0)) parts.push(`${s}s`);

        return parts.join(' ');
    };

    const formatNumber = (num?: number | null) => {
        if (num === null || num === undefined) return '';
        if (num >= 1e9) return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
        if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
        if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
        return num.toString();
    };

    const getRefreshStatusInfo = (status: string | null | undefined) => {
        switch ((status || '').toUpperCase()) {
            case 'RUNNING':
                return { label: 'Refreshing', className: 'text-blue-500', spinning: true };
            case 'SUCCESS':
                return { label: 'Refreshed', className: 'text-emerald-500', spinning: false };
            case 'FAILED':
                return { label: 'Failed', className: 'text-red-500', spinning: false };
            default:
                return null;
        }
    };


    const headerActions = (
        <div className="flex gap-2">
            <input type="file" ref={importInputRef} onChange={handleImportJSON} accept=".json" className="hidden" />
            {hasPermission(user, 'data_models:import') && (
                <CommonButton onClick={() => importInputRef.current?.click()} sx={{ height: 30 }} className="bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700 font-medium">
                    <Upload className="mr-2 h-4 w-4" /> IMPORT JSON
                </CommonButton>
            )}
            {(hasPermission(user, 'data_models:create') || hasPermission(user, 'data_models:manage')) && (
                <CommonButton onClick={handleCreate} sx={{ height: 30 }}>
                    <PlusCircle className="mr-2 h-4 w-4" /> ADD MODEL
                </CommonButton>
            )}
        </div>
    );

    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <input
                    type="text"
                    placeholder="Search models..."
                    className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 py-2 pl-10 pr-4 rounded-xl shadow-sm text-sm focus:ring-2 focus:ring-blue-100 dark:text-slate-100 transition-shadow outline-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            <Button variant="ghost" size="icon" onClick={() => fetchDatabases()} className="text-slate-500 dark:text-slate-400 hover:text-[#002F5D] hover:bg-slate-100 dark:hover:bg-slate-800 h-8 w-8">
                <RefreshCw className="h-4 w-4" />
            </Button>

            <div className="flex items-center bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1">
                <Button
                    variant={view === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${view === 'grid' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setView('grid')}
                >
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                    variant={view === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${view === 'list' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setView('list')}
                >
                    <List className="w-4 h-4" />
                </Button>
            </div>
            <SortDropdown value={sortOption} onChange={(v) => setSortOption(v as any)} />
        </div>
    );

    const handleExport = async (id: string, name: string, e: any) => {
        e.stopPropagation();
        try {
            // VITE_API_BASE_URL already contains /api/v1 — do NOT prepend /api again
            const apiBase = (import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/+$/, '');
            const response = await fetch(`${apiBase}/datamodels/${id}/export`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${name.replace(/\s+/g, '_')}_datamodel_export.json`;
            anchor.click();
            URL.revokeObjectURL(url);
            addNotification('Data Model exported successfully', 'success');
        } catch (error: any) {
            console.error('Export failed:', error);
            addNotification(error.message || 'Failed to export data model', 'error');
        }
    };

    const handleAction = (action: string, id: string, e: any, db?: DatabaseItem) => {
        e.stopPropagation();
        if (action === 'edit') handleEdit(id);
        if (action === 'archive') handleArchive(id);
        if (action === 'delete') handleHardDelete(id);
        if (action === 'refresh') handleRefresh(id);
        if (action === 'schedule' && db) handleOpenSchedule(db);
    };

    return (
        <StandardPageLayout
            title="Data Models"
            description="Manage your semantic data models. Define relationships, measures, Protected Data rules, and refresh schedules."
            headerActions={headerActions}
            filterControls={filterControls}
        >
            {isLoading ? (
                <div className="flex-1 flex items-center justify-center h-64">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#002F5D] border-t-transparent" />
                        Loading data models...
                    </div>
                </div>
            ) : sortedDatabases.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-3xl bg-gray-50/50 dark:bg-slate-900/50 mt-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-full shadow-sm mb-4">
                        <Database className="h-8 w-8 text-gray-300 dark:text-slate-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">No data models found</h3>
                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-2 max-w-xs">Create your first data model to start defining your business logic.</p>
                    {(hasPermission(user, 'data_models:create') || hasPermission(user, 'data_models:manage')) && (
                        <Button
                            variant="ghost"
                            onClick={handleCreate}
                            className="mt-6 text-[#002F5D] hover:bg-blue-50 dark:hover:bg-slate-800"
                        > Create Data Model <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    )}
                </div>
            ) : view === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start pb-10">
                    {sortedDatabases.map((db) => (
                        <div
                            key={db.id}
                            onClick={() => handleEdit(db.id)}
                            className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden"
                        >
                            {/* Decorative grad */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#002F5D] to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center justify-center p-2.5 bg-blue-50 dark:bg-slate-800 rounded-xl group-hover:bg-[#002F5D] group-hover:text-white transition-colors duration-300">
                                    <Database className="h-5 w-5 text-blue-600 dark:text-blue-400 group-hover:text-white" />
                                </div>
                                <div className="flex gap-2 items-center">
                                    {/* Refresh status badge */}
                                    {(() => {
                                        const refreshInfo = getRefreshStatusInfo(db.current_refresh_status);
                                        return refreshInfo ? (
                                            <div 
                                                onClick={(e) => e.stopPropagation()}
                                                className={`cursor-default flex items-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium shadow-sm ${refreshInfo.spinning ? 'bg-[#002F5D] text-white' : (refreshInfo.label === 'Refreshed' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800')}`}
                                            >
                                                <RefreshCw className={`w-3 h-3 ${refreshInfo.spinning ? 'animate-spin' : ''}`} />
                                                {refreshInfo.label}
                                            </div>
                                        ) : null;
                                    })()}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                                            {(hasPermission(user, 'data_models:update') || hasPermission(user, 'data_models:manage')) && (
                                                <DropdownMenuItem onClick={(e) => handleAction('edit', db.id, e)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyToClipboard(db.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                <Copy className="w-4 h-4 mr-2" /> Copy ID
                                            </DropdownMenuItem>
                                            {db.storage_mode === 'import' && (
                                                <>
                                                    {hasPermission(user, 'data_models:refresh') && (
                                                        <DropdownMenuItem
                                                            disabled={refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING'}
                                                            onClick={(e) => { e.preventDefault(); if (!(refreshingIds.has(db.id))) handleAction('refresh', db.id, e); }}
                                                            className={`cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800 ${refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING' ? 'opacity-50 pointer-events-none' : ''}`}
                                                        >
                                                            <RefreshCw className={`w-4 h-4 mr-2 ${refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING' ? 'animate-spin text-blue-500' : ''}`} />
                                                            {refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING' ? 'Refreshing...' : 'Refresh'}
                                                        </DropdownMenuItem>
                                                    )}
                                                    {(hasPermission(user, 'data_models:update') || hasPermission(user, 'data_models:manage')) && (
                                                        <DropdownMenuItem onClick={(e) => handleAction('schedule', db.id, e, db)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                            <Clock className="w-4 h-4 mr-2" /> Schedule
                                                        </DropdownMenuItem>
                                                    )}
                                                </>
                                            )}
                                            {hasPermission(user, 'data_models:export') && (
                                                <DropdownMenuItem onClick={(e) => handleExport(db.id, db.name, e)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Download className="w-4 h-4 mr-2" /> Export JSON
                                                </DropdownMenuItem>
                                            )}
                                            {(hasPermission(user, 'data_models:delete') || hasPermission(user, 'data_models:manage')) && (
                                                <DropdownMenuItem onClick={(e) => handleAction('archive', db.id, e)} className="text-orange-600 dark:text-orange-400 cursor-pointer focus:text-orange-600 dark:focus:text-orange-400 focus:bg-orange-50 dark:focus:bg-orange-900/20">
                                                    <Trash2 className="w-4 h-4 mr-2" /> Archive
                                                </DropdownMenuItem>
                                            )}
                                            {(hasPermission(user, 'data_models:delete') || hasPermission(user, 'data_models:manage')) && (
                                                <DropdownMenuItem onClick={(e) => handleAction('delete', db.id, e)} className="text-red-600 dark:text-red-400 cursor-pointer focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20">
                                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                </DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h3 className="font-bold text-gray-900 dark:text-slate-100 mb-1 group-hover:text-[#002F5D] dark:group-hover:text-blue-400 transition-colors truncate" title={db.name}>{db.name}</h3>
                                <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 min-h-[2.5em]">
                                    {db.description || 'No description provided'}
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="text-center p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                    <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{db.tables?.length || 0}</div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400">Tables</div>
                                </div>
                                <div className="text-center p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                    <div className="text-sm font-bold text-purple-600 dark:text-purple-400">{db.relationships?.length || 0}</div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400">Rels</div>
                                </div>
                                <div className="text-center p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                    <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{db.measures?.length || 0}</div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400">KPIs</div>
                                </div>
                            </div>

                            <div className="pt-3 border-t border-gray-50 dark:border-slate-800 flex flex-col gap-2">
                                <div className="flex items-center text-[10px] text-slate-400 dark:text-slate-500" title={formatDate(db.last_refreshed_at) || 'Never refreshed'}>
                                    <Clock className="w-3 h-3 mr-1" />
                                    Last Refresh: {db.last_refreshed_at ? formatDate(db.last_refreshed_at) : 'Never'}
                                </div>
                                {(db.last_refresh_rows !== null && db.last_refresh_rows !== undefined) && (
                                    <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                                        <span className="flex items-center gap-1" title={`${db.last_refresh_rows.toLocaleString()} Rows extracted`}>
                                            <List className="w-3 h-3 text-slate-400" />
                                            {formatNumber(db.last_refresh_rows)} Rows
                                        </span>
                                        {db.last_refresh_duration !== null && db.last_refresh_duration !== undefined && (
                                            <span className="flex items-center gap-1" title="Time taken to extract">
                                                <Timer className="w-3 h-3 text-slate-400" />
                                                {formatDuration(db.last_refresh_duration)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="flex items-center justify-between text-xs text-slate-400 dark:text-slate-500 mt-1">
                                    <div className="flex gap-2 items-center">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] border ${getStatusBadge(db.status)}`}>
                                            {db.status}
                                        </span>
                                        {(refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING') && (
                                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">
                                                <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                                                Refreshing
                                            </span>
                                        )}
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] border ${getStorageModeBadge(db.storage_mode)}`}>
                                            {db.storage_mode === 'import' ? 'Cached' : 'Direct'}
                                        </span>
                                        {db.domain_id && (
                                            <span
                                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20 max-w-[100px]"
                                                title={`Mapped to Knowledge Base: ${db.domain_name || 'Unknown'}`}
                                            >
                                                <Brain className="w-2.5 h-2.5 flex-shrink-0" />
                                                <span className="truncate">{db.domain_name || 'KB Domain'}</span>
                                            </span>
                                        )}
                                        {db.pd_applier_config && Object.keys(db.pd_applier_config).length > 0 && (
                                            <span
                                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 max-w-[100px]"
                                                title="Protected Data rules applied"
                                            >
                                                <ShieldCheck className="w-2.5 h-2.5 flex-shrink-0" />
                                                <span className="truncate">PD Applied</span>
                                            </span>
                                        )}
                                        {db.source_type && (
                                            <span
                                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 max-w-[100px]"
                                                title={`Origin: ${db.source_type.replace('_', ' ')}`}
                                            >
                                                <Database className="w-2.5 h-2.5 flex-shrink-0" />
                                                <span className="truncate">{db.source_type.replace('_', ' ')}</span>
                                            </span>
                                        )}
                                        {db.datasource_type && (
                                            <span
                                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20 max-w-[100px]"
                                                title={`Data Source: ${db.datasource_name || db.datasource_type}`}
                                            >
                                                <Server className="w-2.5 h-2.5 flex-shrink-0" />
                                                <span className="truncate">{db.datasource_type}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                            <TableRow className="border-b border-gray-200 dark:border-slate-800">
                                <TableHead className="w-[35%] text-gray-500 dark:text-slate-400">Name</TableHead>
                                <TableHead className="text-gray-500 dark:text-slate-400">Type</TableHead>
                                <TableHead className="text-gray-500 dark:text-slate-400">Stats</TableHead>
                                <TableHead className="text-gray-500 dark:text-slate-400">Status</TableHead>
                                <TableHead className="text-gray-500 dark:text-slate-400">Last Refreshed</TableHead>
                                <TableHead className="text-right text-gray-500 dark:text-slate-400">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedDatabases.map((db) => (
                                <TableRow
                                    key={db.id}
                                    className="cursor-pointer hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800"
                                    onClick={() => handleEdit(db.id)}
                                >
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-blue-600 dark:text-blue-400">
                                                <Database className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-slate-100">{db.name}</div>
                                                <div className="text-xs text-gray-500 dark:text-slate-400 truncate max-w-[200px]">
                                                    {db.description || "No description"}
                                                </div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${getStorageModeBadge(db.storage_mode)}`}>
                                                {db.storage_mode === 'import' ? 'Cached' : 'Direct Query'}
                                            </span>
                                            {db.pd_applier_config && Object.keys(db.pd_applier_config).length > 0 && (
                                                <span
                                                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                                                    title="Protected Data rules applied"
                                                >
                                                    <ShieldCheck className="w-3 h-3 flex-shrink-0" />
                                                    <span>PD Applied</span>
                                                </span>
                                            )}
                                            {db.source_type && (
                                                <span
                                                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                                                    title={`Origin: ${db.source_type.replace('_', ' ')}`}
                                                >
                                                    <Database className="w-3 h-3 flex-shrink-0" />
                                                    <span>{db.source_type.replace('_', ' ')}</span>
                                                </span>
                                            )}
                                            {db.datasource_type && (
                                                <span
                                                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20"
                                                    title={`Data Source: ${db.datasource_name || db.datasource_type}`}
                                                >
                                                    <Server className="w-3 h-3 flex-shrink-0" />
                                                    <span>{db.datasource_type}</span>
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-3 text-xs text-gray-500 dark:text-slate-400">
                                            <span><b>{db.tables?.length || 0}</b> Tbls</span>
                                            <span><b>{db.relationships?.length || 0}</b> Rels</span>
                                            <span><b>{db.measures?.length || 0}</b> KPIs</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-1 flex-col sm:flex-row">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border w-fit ${getStatusBadge(db.status)}`}>
                                                {db.status}
                                            </span>
                                            {(refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING') && (
                                                <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 w-fit">
                                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                                    Refreshing
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const refreshInfo = getRefreshStatusInfo(db.current_refresh_status);
                                            return (
                                                <div className="flex flex-col gap-0.5">
                                                    {refreshInfo && (
                                                        <span className={`flex items-center gap-1 text-[10px] font-medium ${refreshInfo.spinning ? 'text-blue-500' : (refreshInfo.label === 'Refreshed' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}`}>
                                                            <RefreshCw className={`w-3 h-3 ${refreshInfo.spinning ? 'animate-spin' : ''}`} />
                                                            {refreshInfo.label}
                                                        </span>
                                                    )}
                                                    <span
                                                        className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500"
                                                        title={formatDate(db.last_refreshed_at) || 'Never refreshed'}
                                                    >
                                                        <Clock className="w-3 h-3" />
                                                        Last refreshed on: {db.last_refreshed_at ? formatDate(db.last_refreshed_at) : 'Never'}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </TableCell>

                                    <TableCell className="text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                                                {(hasPermission(user, 'data_models:update') || hasPermission(user, 'data_models:manage')) && (
                                                    <DropdownMenuItem onClick={(e) => handleAction('edit', db.id, e)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                        <Edit className="w-4 h-4 mr-2" /> Edit
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyToClipboard(db.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Copy className="w-4 h-4 mr-2" /> Copy ID
                                                </DropdownMenuItem>
                                                {db.storage_mode === 'import' && (
                                                    <>
                                                        {hasPermission(user, 'data_models:refresh') && (
                                                            <DropdownMenuItem
                                                                disabled={refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING'}
                                                                onClick={(e) => { e.preventDefault(); if (!(refreshingIds.has(db.id))) handleAction('refresh', db.id, e); }}
                                                                className={`cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800 ${refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING' ? 'opacity-50 pointer-events-none' : ''}`}
                                                            >
                                                                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING' ? 'animate-spin text-blue-500' : ''}`} />
                                                                {refreshingIds.has(db.id) || db.current_refresh_status === 'RUNNING' ? 'Refreshing...' : 'Refresh'}
                                                            </DropdownMenuItem>
                                                        )}
                                                        {(hasPermission(user, 'data_models:update') || hasPermission(user, 'data_models:manage')) && (
                                                            <DropdownMenuItem onClick={(e) => handleAction('schedule', db.id, e, db)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                                <Clock className="w-4 h-4 mr-2" /> Schedule
                                                            </DropdownMenuItem>
                                                        )}
                                                    </>
                                                )}
                                                {hasPermission(user, 'data_models:export') && (
                                                    <DropdownMenuItem onClick={(e) => handleExport(db.id, db.name, e)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                        <Download className="w-4 h-4 mr-2" /> Export JSON
                                                    </DropdownMenuItem>
                                                )}
                                                {(hasPermission(user, 'data_models:delete') || hasPermission(user, 'data_models:manage')) && (
                                                    <DropdownMenuItem onClick={(e) => handleAction('archive', db.id, e)} className="text-orange-600 dark:text-orange-400 cursor-pointer focus:text-orange-600 dark:focus:text-orange-400 focus:bg-orange-50 dark:focus:bg-orange-900/20">
                                                        <Trash2 className="w-4 h-4 mr-2" /> Archive
                                                    </DropdownMenuItem>
                                                )}
                                                {(hasPermission(user, 'data_models:delete') || hasPermission(user, 'data_models:manage')) && (
                                                    <DropdownMenuItem onClick={(e) => handleAction('delete', db.id, e)} className="text-red-600 dark:text-red-400 cursor-pointer focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20">
                                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>

                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
            }

            {/* ── Schedule Refresh Dialog ── */}
            <MuiDialog open={isScheduleOpen} onClose={() => setIsScheduleOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>Schedule Refresh</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {/* Tab Bar */}
                    <div className="flex border-b border-gray-200 dark:border-slate-700">
                        {(['recurring', 'interval', 'custom'] as const).map(tab => (
                            <button
                                key={tab}
                                onClick={() => setScheduleTab(tab)}
                                className={`px-5 py-3 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${scheduleTab === tab
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>

                    <div className="p-5 space-y-4">
                        {/* ── RECURRING ── */}
                        {scheduleTab === 'recurring' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Frequency</label>
                                    <select
                                        value={scheduleFrequency}
                                        onChange={e => setScheduleFrequency(e.target.value)}
                                        className="w-full border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="hourly">Hourly</option>
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>

                                {scheduleFrequency !== 'hourly' && (
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Time (UTC)</label>
                                        <div className="flex gap-1 items-center">
                                            {/* Hour: 00–23 */}
                                            <select
                                                value={scheduleTime ? scheduleTime.split(':')[0] : '09'}
                                                onChange={e => {
                                                    const mm = scheduleTime ? scheduleTime.split(':')[1] : '00';
                                                    setScheduleTime(`${e.target.value}:${mm}`);
                                                }}
                                                className="flex-1 border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                            >
                                                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                                                    <option key={h} value={h}>{h}</option>
                                                ))}
                                            </select>
                                            <span className="text-gray-400 font-bold">:</span>
                                            {/* Minute: every 5 min */}
                                            <select
                                                value={scheduleTime ? String(Math.round(parseInt(scheduleTime.split(':')[1] || '0') / 5) * 5 % 60).padStart(2, '0') : '00'}
                                                onChange={e => {
                                                    const hh = scheduleTime ? scheduleTime.split(':')[0] : '09';
                                                    setScheduleTime(`${hh}:${e.target.value}`);
                                                }}
                                                className="flex-1 border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                            >
                                                {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(m => (
                                                    <option key={m} value={m}>{m}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {scheduleFrequency === 'weekly' && (
                                    <div className="space-y-1 col-span-2">
                                        <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Day of Week</label>
                                        <select
                                            value={scheduleWeekDay}
                                            onChange={e => setScheduleWeekDay(e.target.value)}
                                            className="w-full border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                        >
                                            <option value="0">Sunday</option>
                                            <option value="1">Monday</option>
                                            <option value="2">Tuesday</option>
                                            <option value="3">Wednesday</option>
                                            <option value="4">Thursday</option>
                                            <option value="5">Friday</option>
                                            <option value="6">Saturday</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── INTERVAL ── */}
                        {scheduleTab === 'interval' && (
                            <div className="flex items-end gap-3">
                                <div className="space-y-1 flex-1">
                                    <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Every</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={scheduleIntervalValue}
                                        onChange={e => setScheduleIntervalValue(Math.max(1, parseInt(e.target.value) || 1))}
                                        className="w-full border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div className="space-y-1 flex-1">
                                    <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Unit</label>
                                    <select
                                        value={scheduleIntervalUnit}
                                        onChange={e => setScheduleIntervalUnit(e.target.value as 'hours' | 'minutes')}
                                        className="w-full border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="minutes">Minutes</option>
                                        <option value="hours">Hours</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* ── CUSTOM ── */}
                        {scheduleTab === 'custom' && (
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Cron Expression</label>
                                <input
                                    type="text"
                                    placeholder="0 9 * * *"
                                    value={cronExpression}
                                    onChange={e => setCronExpression(e.target.value)}
                                    className="w-full border border-gray-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm font-mono bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                                <p className="text-[11px] text-gray-400 dark:text-slate-500">Format: minute hour day month day_of_week</p>
                            </div>
                        )}

                        {/* Cron preview */}
                        <div className="pt-2 border-t border-gray-100 dark:border-slate-800">
                            <p className="text-[11px] text-gray-400 dark:text-slate-500">
                                Cron: <span className="font-mono text-blue-500">{buildCronFromUI() || '—'}</span>
                            </p>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            if (!scheduleDbId) return;
                            try {
                                await apiPost(`/datamodels/${scheduleDbId}/schedule`, token, { cron_expression: '', timezone: 'UTC' });
                                setIsScheduleOpen(false);
                                addNotification('Schedule cleared', 'success');
                                fetchDatabases();
                            } catch { addNotification('Failed to clear schedule', 'error'); }
                        }}
                    >
                        Clear Schedule
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button variant="ghost" onClick={() => setIsScheduleOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveSchedule} className="bg-blue-600 hover:bg-blue-700 text-white">
                        Save changes
                    </Button>
                </DialogActions>
            </MuiDialog>

            {/* Confirm Deletion Modal */}
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                title="Archive Data Model"
                onConfirm={confirmArchive}
                onCancel={() => {
                    setIsDeleteModalOpen(false);
                    setDbToDelete(null);
                }}
                message="Are you sure you want to archive this data model? For cached models, materialized data will be removed from storage, but the model definition will be archived to trash."
            />

            {/* Confirm Hard Deletion Modal */}
            <ConfirmModal
                isOpen={isHardDeleteModalOpen}
                title="Permanently Delete Data Model"
                onConfirm={confirmHardDelete}
                onCancel={() => {
                    setIsHardDeleteModalOpen(false);
                    setDbToHardDelete(null);
                }}
                message="Are you sure you want to permanently delete this data model? This action cannot be undone."
            />

            {/* Blocking Dialog — shown when deletion is blocked by published dashboards */}
            <MuiDialog
                open={isBlockingDialogOpen}
                onClose={() => setIsBlockingDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 1, color: '#b91c1c' }}>
                    ⛔ Cannot Delete Data Model
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            {blockingMessage}
                        </Typography>
                        <Box>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
                                PUBLISHED DASHBOARDS THAT MUST BE UNPUBLISHED FIRST:
                            </Typography>
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'error.light',
                                    borderRadius: 1,
                                    bgcolor: 'error.50',
                                    p: 1.5,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                }}
                            >
                                {blockingDashboards.map((d) => (
                                    <Box
                                        key={d.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 0.5,
                                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                                            '&:last-child': { borderBottom: 'none' },
                                        }}
                                    >
                                        <span style={{ fontSize: 14 }}>📊</span>
                                        <Typography variant="body2" fontWeight={500}>
                                            {d.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontFamily: 'monospace' }}>
                                            {d.id.slice(0, 8)}…
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Go to each dashboard → click <strong>⋮ Actions → Unpublish</strong>, then remove the data model from the widgets, and finally delete it.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setIsBlockingDialogOpen(false)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        Got it
                    </Button>
                </DialogActions>
            </MuiDialog>

            {/* ── Connection Picker Dialog ── */}
            <MuiDialog
                open={isConnectionPickerOpen}
                onClose={() => setIsConnectionPickerOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>
                    Select Connection
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            The original connection used by this data model was not found in this
                            environment. Please select a replacement connection to continue importing.
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                            <Typography variant="caption" color="text.disabled" display="block" mb={0.5}>
                                Original connection ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                                {missingConnection}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.75} fontWeight={600}>
                                Map to connection
                            </Typography>
                            <select
                                value={selectedConnection}
                                onChange={(e) => setSelectedConnection(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 8,
                                    border: '1px solid #e2e8f0',
                                    fontSize: 14,
                                    background: 'white',
                                    outline: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="">-- Select a connection --</option>
                                {availableConnections.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}{c.source === 'global' ? ' (Global)' : ''}
                                    </option>
                                ))}
                            </select>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <button
                        onClick={() => setIsConnectionPickerOpen(false)}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: 14 }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConnectionPickerConfirm}
                        disabled={!selectedConnection}
                        style={{
                            padding: '8px 20px', borderRadius: 8, border: 'none',
                            background: selectedConnection ? '#002F5D' : '#cbd5e1',
                            color: 'white', cursor: selectedConnection ? 'pointer' : 'not-allowed',
                            fontWeight: 600, fontSize: 14
                        }}
                    >
                        Import
                    </button>
                </DialogActions>
            </MuiDialog>
        </StandardPageLayout>
    );
};
